import { motion } from 'framer-motion';
import { Power, Infinity as InfinityIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { RelayState, DeviceStatus } from '@/lib/types';
import { RelayConfigPopover } from './RelayConfigPopover';

interface Props {
  device: DeviceStatus;
  index: number;
  relay: RelayState;
  pending?: boolean;
  disabled?: boolean;
  onToggle: (next: 0 | 1) => void;
}

// The WOW component. Tap to toggle. State change → LED bloom. Optimistic feel.
// Hover/focus → settings cog appears for per-relay config.
export function RelayTile({ device, index, relay, pending, disabled, onToggle }: Props) {
  const on = !!relay.state;
  const enabled = relay.enabled !== false;
  const number = relay.no ?? index + 1;
  const name = relay.name || `Relay ${number}`;
  const alwaysOn = !!relay.alwaysOn;
  const actSel = relay.actTimeSelect ?? 1;

  return (
    <div className="relative group">
      <motion.button
        type="button"
        disabled={disabled || !enabled}
        whileTap={{ scale: disabled ? 1 : 0.97 }}
        onClick={() => onToggle(on ? 0 : 1)}
        className={cn(
          'w-full text-left p-3 rounded-xl border transition-colors',
          'flex flex-col gap-2 min-h-[88px]',
          on
            ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
            : 'bg-white border-slate-200 hover:border-slate-300',
          !enabled && 'opacity-40 cursor-not-allowed',
          disabled && 'cursor-not-allowed opacity-60',
          pending && 'ring-2 ring-amber-300 ring-offset-1',
        )}
        aria-pressed={on}
        aria-label={`${name} — ${on ? 'on' : 'off'}, click to ${on ? 'turn off' : 'turn on'}`}
      >
        <div className="flex items-center justify-between">
          <span className={cn(
            'text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded',
            on ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600',
          )}>
            {String(number).padStart(2, '0')}
          </span>
          <motion.span
            animate={{ scale: on ? [1, 1.4, 1] : 1 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className={cn(
              'w-2.5 h-2.5 rounded-full',
              on ? 'bg-ok shadow-[0_0_10px_rgba(16,185,129,0.7)]' : 'bg-slate-300',
            )}
          />
        </div>
        <h4 className="text-xs font-semibold text-ink leading-tight line-clamp-2">{name}</h4>
        <div className="mt-auto flex items-center justify-between gap-1">
          <div className={cn(
            'flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider',
            on ? 'text-emerald-700' : 'text-slate-400',
          )}>
            <Power className="w-3 h-3" /> {on ? 'on' : 'off'}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            {alwaysOn && <span title="Always-on" className="text-violet-500"><InfinityIcon className="w-3 h-3" /></span>}
            <span className="font-mono">T{actSel}</span>
          </div>
        </div>
      </motion.button>

      {/* Settings cog — hidden until hover/focus, doesn't intercept the toggle */}
      <RelayConfigPopover device={device} index={index} relay={relay} disabled={!!disabled || !enabled} />
    </div>
  );
}
